import { useCallback } from 'react';
import { CareType } from '~/core/dapi/careTeam';
import history from '~/core/history';
import { isUserProfileAMinor } from '~/core/util/userProfile';
import { useAccount } from '~/hooks/account/useAccount';
import useSelectActiveUserProfile from '~/hooks/useSelectActiveUserProfile';
import { useCareOptions } from './useCareOptions';
import { useSrpSearch } from './useSrpSearch';

/**
 * Hooks that returns a helper function to
 * direct the user to the proper page based on the type of
 * care they are searching for
 *
 * @returns a click handler
 */
export function useGetCareNow(careType: CareType) {
  const { data, showPremiumVisitUpsell, hasRebookOptions } = useCareOptions(careType);

  const isMinor = isUserProfileAMinor(useSelectActiveUserProfile());
  const goToSrpSearch = useSrpSearch(careType, {
    patientType: isMinor ? 'kids' : 'adults',
  });
  const account = useAccount();

  /**
   * Routes the user to the care options page
   */
  const goToCareOptionsPage = useCallback(() => {
    history.push(`/account/care-options/${careType}`);
  }, [careType]);

  return useCallback(() => {
    // if we're logged out, always short-circuit to SRP
    if (!account) {
      return goToSrpSearch();
    }

    // If we're loading, short-circuit to the care-options page
    // so we can give the user all their possible options
    if (!data) {
      return goToCareOptionsPage();
    }

    // Rules for video and uc searches
    if (careType === 'video' || careType === 'uc') {
      if (showPremiumVisitUpsell || hasRebookOptions) {
        return goToCareOptionsPage();
      }
      return goToSrpSearch();
    }

    // Rules for covid searches
    if (careType === 'covid') {
      if (hasRebookOptions) {
        return goToCareOptionsPage();
      }
      return goToSrpSearch();
    }

    // Fallback that shouldn't be triggered
    return goToCareOptionsPage();
  }, [
    account,
    careType,
    data,
    goToCareOptionsPage,
    goToSrpSearch,
    hasRebookOptions,
    showPremiumVisitUpsell,
  ]);
}
